import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useTelegramConnection = () => {
  const { toast } = useToast();
  const [connectingTelegram, setConnectingTelegram] = useState(false);
  const [telegramDeeplink, setTelegramDeeplink] = useState<string | null>(null);
  const [connectionAttempted, setConnectionAttempted] = useState(false);

  const extractLastPathSegment = (path: string) => {
    // Remove leading slash and split the path
    const segments = path.replace(/^\//, '').split('/');
    // Return the last segment
    return segments[segments.length - 1];
  };

  const connectTelegram = async () => {
    try {
      setConnectingTelegram(true);
      setTelegramDeeplink(null);
      setConnectionAttempted(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }
      
      // Get the current page path and extract last segment
      const currentPath = window.location.pathname;
      const lastPathSegment = extractLastPathSegment(currentPath);
      
      // Call the Edge Function with the page path included
      const { data: telegramData, error: telegramError } = await supabase.functions.invoke('telegram-bot', {
        body: {
          userId: user.id,
          pagePath: currentPath,
          lastPathSegment: lastPathSegment,
          action: 'connect_telegram',
          timestamp: new Date().toISOString(),
          source: 'settings-page'
        }
      });
      
      if (telegramError) {
        console.error("Error invoking telegram-bot function:", telegramError);
        throw telegramError;
      }
      
      console.log("Telegram bot function response:", telegramData);
      
      // Use the deeplink from the edge function response
      const deepLink = telegramData.deeplink;
      console.log("Received deeplink from edge function:", deepLink);
      
      // For settings/general page, ensure "general" is added to the end if not already present
      console.log("Current path:", currentPath);
      
      if (currentPath.includes('/dashboard/settings/general') && !deepLink.endsWith('general')) {
        console.log("Adding 'general' suffix to deeplink for general settings page");
        const modifiedDeepLink = `${deepLink}general`;
        setTelegramDeeplink(modifiedDeepLink);
        
        const newWindow = window.open(modifiedDeepLink, '_blank');
        
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          setTimeout(() => {
            window.location.href = modifiedDeepLink;
          }, 500);
        }
      } else {
        setTelegramDeeplink(deepLink);
        
        const newWindow = window.open(deepLink, '_blank');
        
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          setTimeout(() => {
            window.location.href = deepLink;
          }, 500);
        }
      }
      
      toast({
        title: "Telegram Connection",
        description: "Opening Telegram. Please click START to complete the connection.",
      });
      
      return true;
    } catch (error) {
      console.error("Error connecting to Telegram:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to connect to Telegram. Please try again.",
      });
      setConnectionAttempted(false);
      return false;
    } finally {
      setConnectingTelegram(false);
    }
  };

  const openTelegramDirectly = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get the current page path and extract last segment
      const currentPath = window.location.pathname;
      const lastPathSegment = extractLastPathSegment(currentPath);
      
      // Call the Edge Function with the page path included
      const { data: telegramData, error: telegramError } = await supabase.functions.invoke('telegram-bot', {
        body: {
          userId: user.id,
          pagePath: currentPath,
          lastPathSegment: lastPathSegment,
          action: 'connect_telegram',
          timestamp: new Date().toISOString(),
          source: 'settings-page'
        }
      });
      
      if (telegramError) {
        console.error("Error invoking telegram-bot function:", telegramError);
        return false;
      } else {
        console.log("Telegram bot function response:", telegramData);
      }
      
      // Use the deeplink from the edge function response
      const deepLink = telegramData.deeplink;
      console.log("Received deeplink from edge function:", deepLink);
      
      // Check if we're on the settings/general page and add suffix if needed
      console.log("Current path:", currentPath);
      let finalDeepLink = deepLink;
      
      if (currentPath.includes('/dashboard/settings/general') && !deepLink.endsWith('general')) {
        console.log("Adding 'general' suffix to deeplink for general settings page");
        finalDeepLink = `${deepLink}general`;
      }
      
      const newWindow = window.open(finalDeepLink, '_blank');
      
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        setTimeout(() => {
          window.location.href = finalDeepLink;
        }, 500);
      }

      return true;
    } catch (error) {
      console.error('Error opening Telegram:', error);
      return false;
    }
  };

  return {
    connectingTelegram,
    telegramDeeplink,
    connectionAttempted,
    connectTelegram,
    openTelegramDirectly
  };
};
